import * as Sentry from "@sentry/browser";

import { appVersion, trackEndpoint } from "environment";

import Cookies from "js-cookie";
import env from "@beam-australia/react-env";
import i18n from "./i18n";
import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";

const axios = require("axios");

// import { googleAnalyticsId } from "environment";

const puckEvents = [
  "Visited JobPage Public",
  "JobPage Time Spent",
  "JobPage Player Time Spent",
  "JobPage Public Submitted Short Form",
  "Application Found",

  "Visited ContentPage Public",
  "ContentPage Public Time Spent",
  "ContentPage Player Time Spent",
  "ContentPage Email Submitted",
  "ContentPage Lead Form Submitted",
  "Sourcing Page Feedback Submitted",

  "Visited JobClip Public",
  "JobClip Time Spent",
  "JobClip Player Time Spent",

  "Person Created",

  "Person Sequence Enrolled",
  "Person Sequence Unenrolled",
  "Person Sequence Replied",

  "Email Sent",
  "Email Open",
  "Email Click",
  "Email Bounce",
  "Email Send Failed",
  "Email Dropped",
];

const preProcessTrackingData = (data) => {
  // Set default category metadata
  data = Object.assign(
    { category: "general", userAgent: window.navigator.userAgent },
    data
  );

  // set common JobPage parameters
  if (data.jobPage) {
    data = Object.assign(
      omitBy(
        {
          permalink: data.jobPage.permalink,
          title: data.jobPage.title,
          employer: data.jobPage.employerProfile?.name,
          employerPermalink: data.jobPage.employerProfile?.permalink,
          employerName: data.jobPage.employerProfile?.name,
          platform: data.jobPage.jobBoard.atsSourcePlatform,
          department:
            data.jobPage.departments?.[0]?.name || data.jobPage.department,
          location: data.jobPage.location,
          office: data.jobPage.offices?.[0]?.name || data.jobPage.office,
        },
        isNil
      ),
      data
    );
    delete data.jobPage;
  }

  // set common JobClip parameters
  if (data.jobClip) {
    data = Object.assign(
      omitBy(
        {
          permalink: data.jobClip.permalink,
          jobClipType: data.jobClip.type,
          employer:
            data.jobClip.employerProfile?.name || data.jobClip.employerName,
          employerPermalink: data.jobClip.employerProfile?.permalink,
          employerName:
            data.jobClip.employerProfile?.name || data.jobClip.employerName,
        },
        isNil
      ),
      data
    );
    delete data.jobClip;
  }

  // set common ContentPage parameters
  if (data.contentPage) {
    data = Object.assign(
      omitBy(
        {
          permalink: data.contentPage.permalink,
          title: data.contentPage.title,
          employer: data.contentPage.employerProfile?.name,
          employerPermalink: data.contentPage.employerProfile?.permalink,
          employerName: data.contentPage.employerProfile?.name,
          blockCount: data.contentPage.blocks?.length,
          sourceTemplateTitle: data.contentPage.type,
          type: data.contentPage.type,
        },
        isNil
      ),
      data
    );
    delete data.contentPage;
  }

  // set common User parameters
  if (data.user) {
    data = Object.assign(
      omitBy(
        {
          email: data.user.email,
          firstName: data.user.profile?.firstName,
          lastName: data.user.profile?.lastName,
        },
        isNil
      ),
      data
    );
    if (data.user.employerProfile) {
      data = Object.assign(
        omitBy(
          {
            employer: data.user.employerProfile.name,
            employerPermalink: data.user.employerProfile.permalink,
            employerName: data.user.employerProfile.name,
            subscriptionTier: data.user.employerProfile.subscriptionTier,
            isLiveWithJobPages: data.user.employerProfile.isLiveWithJobPages,
            sourcingTool: data.user.employerProfile.settings?.sourcingTool,
          },
          isNil
        ),
        data
      );
    }
    delete data.user;
  }

  // handle special query-parameters
  if (data.queryParams) {
    const mapping = {
      shareLink: "p-s",
      shareLinkFirstName: "p-fn",
      shareLinkLastName: "p-ln",
      shareLinkFullname: "p-un",
      shareLinkJobTitle: "p-jt",
      shareLinkEmail: "p-e",
      shareable: "shareable",

      // salary calc params
      query: "query",
      locationString: "locationString",
      lat: "lat",
      lng: "lng",
      isRemote: "isRemote",
      distanceMi: "distanceMi",
      searchTerm: "searchTerm",
      resultsJobTitleFamilyName: "resultsJobTitleFamilyName",
      resultsJobTitleFamilyTopJobTitle: "resultsJobTitleFamilyTopJobTitle",
      resultsQuery: "resultsQuery",
      showFamilies: "_showFamilies",
    };
    for (const key in mapping) {
      if (data.queryParams[mapping[key]]) {
        data[key] = data.queryParams[mapping[key]];
      }
    }
    delete data.queryParams;
  }

  if (data.i18n) {
    data = Object.assign(
      omitBy(
        {
          language: i18n.language,
        },
        isNil
      ),
      data
    );
    delete data.i18n;
  }

  return data;
};

const shouldBlockAnalytics = () => {
  return hasBlockingCookie() || env("DEBUG_ANALYTICS");
};

const mockAnalytics = {
  page: function (...args) {
    console.log("analytics.page", args);
  },
  track: function (eventName, data = {}, options = {}, callback) {
    data = preProcessTrackingData(data);
    console.log("analytics.track", eventName, data, options);
    callback && callback();
  },
  identify: function (userId, data = {}, options = {}, callback) {
    data = preProcessTrackingData(data);
    if (!userId && global.analytics?.user) {
      userId = global.analytics.user()?.anonymousId();
    }
    console.log("analytics.identify", userId, data, options);
    callback && callback();
  },
  user: function () {
    console.log("analytics.user()");
    return {
      anonymousId: () => "debug-analytics-anonymousId",
    };
  },
  amplitudeDeviceId: function () {
    return "amplitude-deviceId";
  },
};

// These can't be arrow function or "...arguments" won't work
export const analytics = {
  // Page view tracking
  page: function (pageName, category, properties = {}) {
    if (shouldBlockAnalytics()) {
      return mockAnalytics.page(...arguments);
    }

    // Set default properties
    properties = Object.assign({}, properties);

    if (pageName === undefined) {
      // automatically detect page name/title/etc
      return global.analytics?.page();
    } else if (category === undefined) {
      // page name only, no category provided
      return global.analytics?.page("general", pageName, properties);
    } else {
      // page name & category
      return global.analytics?.page(category, pageName, properties);
    }
  },

  // Event tracking
  track: function (eventName, data = {}, options = {}, callback) {
    if (shouldBlockAnalytics()) {
      return mockAnalytics.track(...arguments);
    }

    data = preProcessTrackingData(data);

    // Send whitelisted events to Puck
    if (puckEvents.includes(eventName)) {
      const anonymousId =
        global.analytics?.user &&
        global.analytics.user() &&
        global.analytics.user().anonymousId();

      if (!data.employerPermalink) {
        console.error("Missing employerPermalink in track event");
        Sentry.captureException(
          new Error("Missing employerPermalink in track event")
        );
      } else if (!anonymousId) {
        console.error("Missing anonymousId in track event");
      } else {
        axios
          .post(`${trackEndpoint}/track`, {
            ...data,
            event: eventName,
            anonymousId,
            userAgent: window.navigator.userAgent,
          })
          .catch((error) => {
            if (error.response && error.response.status === 503) {
              console.log("Ignoring 503 error:", error);
            } else {
              console.error("Failed to send event to Puck", error);
              Sentry.captureException("Failed to send event to Puck", error);
            }
          });
      }
    }

    if (global.analytics) {
      global.analytics.track(eventName, data, options);
    }

    if (callback) setTimeout(callback, 250);
  },

  // User identification
  identify: function (userId, data = {}, options = {}, callback) {
    if (shouldBlockAnalytics()) {
      return mockAnalytics.identify(...arguments);
    }

    data = preProcessTrackingData(data);

    if (!userId && global.analytics?.user) {
      userId = global.analytics.user()?.anonymousId();
    }
    data.userAgent = window.navigator.userAgent;

    options.context = options.context || {};
    options.context.app = options.context.app || {};
    options.context.app.version = appVersion;

    if (global.analytics) {
      global.analytics.identify(userId, data, options);
    }

    Sentry.setUser({
      id: userId,
      ...data,
    });

    if (global.FS) {
      const displayName =
        data.firstName || data.lastName
          ? `${data.firstName} ${data.lastName}`
          : data.email;
      global.FS.identify(userId, {
        displayName,
        ...data,
      });
    }

    if (callback) setTimeout(callback, 250);
  },

  user: function () {
    if (shouldBlockAnalytics()) {
      return mockAnalytics.user(...arguments);
    }

    return global.analytics?.user();
  },

  amplitudeDeviceId: function () {
    if (shouldBlockAnalytics()) {
      return mockAnalytics.amplitudeDeviceId(...arguments);
    }

    return global.amplitude?.getInstance()?.options?.deviceId;
  },
};

const docDomain = document.domain || "ngrok.io";
const rootDomain = docDomain.includes("ngrok.io")
  ? docDomain
  : "." + docDomain.split(".").slice(-2).join(".");

const flagStorageName = (flagName) => `puck-flag-${flagName}`;

export const setFlagForced = (flagName, variantName) => {
  return localStorage.setItem(flagStorageName(flagName), variantName);
};

export const getFlagForced = (flagName) => {
  return localStorage.getItem(flagStorageName(flagName));
};

const blockingCookieName = "puck-disable-analytics";

export const setBlockingCookie = (hideHover = false) => {
  console.log("Setting analytics blocking cookie", rootDomain);
  Cookies.set(blockingCookieName, JSON.stringify({ hideHover }), {
    expires: 365,
    domain: rootDomain,
  });
};

export const hasBlockingCookie = () => {
  const disableCookie = Cookies.get(blockingCookieName);
  return !!disableCookie;
};

export const hasVisibleBlockingCookie = () => {
  const disableCookie = Cookies.get(blockingCookieName);
  return disableCookie && !JSON.parse(disableCookie).hideHover;
};
